import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import shuffle from "lodash/shuffle";
import { useMemo } from "react";

import useAutosuggestEmptyQueryV1Experiment from "../hooks/experiments/useAutosuggestEmptyQueryV1Experiment";
import { useAutosuggestV1PlaceholderSuggestions } from "./autosuggestEmptyQueryV1PlaceholderSearches";

type PlaceholderSearchSuggestions = PlaceholderSearchSuggestionsInput & {
  uid: string;
  type: string;
};

type PlaceholderSearchSuggestionsInput = {
  name: string;
  value: string;
  category?: string;
  displayStartDate?: Date;
  displayEndDate?: Date;
};

const NUMBER_OF_RANDOM_SUGGESTIONS = 5;

export const usePlaceholderSuggestions = (
  shouldFilterDates: boolean = true,
): PlaceholderSearchSuggestions[] => {
  const { t } = useTranslation(
    "ui-components-website\\search\\useSearchWithSuggestions",
  );
  const currentYear = dayjs().year();
  const { useNewPlaceholderSuggestions } =
    useAutosuggestEmptyQueryV1Experiment();

  const newPlaceholderSuggestions: PlaceholderSearchSuggestionsInput[] =
    useAutosuggestV1PlaceholderSuggestions();

  const controlPlaceholderSuggestions: PlaceholderSearchSuggestionsInput[] = [
    {
      name: t("Summer Camps"),
      value: "Summer Camps",
      category: "Camps",
      // start showing on june 1st
      displayStartDate: dayjs("1900-06-01").year(currentYear).toDate(),
      // show until july 31st
      displayEndDate: dayjs("1900-07-31").year(currentYear).toDate(),
    },
    {
      name: t("Spanish"),
      value: "Spanish",
      category: "Languages",
    },
    {
      name: t("Writing"),
      value: "Writing",
      category: "English Language Arts",
    },
    {
      name: t("Math"),
      value: "Math",
      category: "Math",
    },
    {
      name: t("Reading"),
      value: "Reading",
      category: "English Language Arts",
    },
    {
      name: t("Coding"),
      value: "Coding",
      category: "Coding & Tech",
    },
    {
      name: t("ADHD"),
      value: "ADHD",
      category: "Health & Wellness",
    },
  ];

  // Always call useMemo unconditionally (Rules of Hooks)
  const randomNewSuggestions = useMemo(
    () =>
      shuffle([...newPlaceholderSuggestions]).slice(
        0,
        NUMBER_OF_RANDOM_SUGGESTIONS,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [], // Only randomize once per session
  );

  const placeholderSearchSuggestions: PlaceholderSearchSuggestionsInput[] =
    useNewPlaceholderSuggestions
      ? randomNewSuggestions
      : controlPlaceholderSuggestions;

  return placeholderSearchSuggestions
    .map((suggestion: PlaceholderSearchSuggestionsInput, index: number) => {
      return {
        ...suggestion,
        uid: (index + 1).toString(),
        type: "placeholder",
      };
    })
    .filter(suggestion => {
      if (!shouldFilterDates) {
        return true;
      }
      return (
        !suggestion.displayStartDate ||
        !suggestion.displayEndDate ||
        (dayjs().isAfter(suggestion.displayStartDate) &&
          dayjs().isBefore(suggestion.displayEndDate))
      );
    });
};
